import {useRouterState} from "@tanstack/react-router";
import {useEffect, useMemo, useRef, useState} from "react";
import {BottomNavMobileCreateDrawer} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/bottomNavMobileCreateDrawer";
import {WorkspaceListCardCreateSidePanel} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/workspaceListCardCreateSidePanel";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {workspaceCreateItemsWithEventContainerId} from "~/utilities/workspaceHelpers";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {AnankeLoadingOverlay} from "~/components/anankeLoadingOverlay";

/**
 * List footer "Create" chrome: bottom sheet on mobile, aside side panel on desktop.
 */
export function WorkspaceListCardCreateChrome({
    opened,
    onClose,
    workspaceId,
    items,
    eventContainerId,
}: {
    opened: boolean;
    onClose: () => void;
    workspaceId: Uuid;
    items: readonly WorkspaceMobileBottomCreateItem[];
    /** When set, every shortcut gets `?eventContainerId=`. */
    eventContainerId?: Uuid | null;
}) {
    const isMobile = useCheckMobile();
    const itemsWithEventContainer = useMemo(
        () => workspaceCreateItemsWithEventContainerId(items, eventContainerId),
        [items, eventContainerId],
    );
    const [createNavPending, setCreateNavPending] = useState(false);
    const pathname = useRouterState({select: (s) => s.location.pathname});
    const prevPathnameRef = useRef(pathname);

    useEffect(() => {
        if (createNavPending === true && pathname !== prevPathnameRef.current) {
            setCreateNavPending(false);
        }
        prevPathnameRef.current = pathname;
    }, [pathname, createNavPending]);

    const handleBeforeCreateNavigate = () => {
        setCreateNavPending(true);
    };

    const loadingOverlay =
        createNavPending === true ? (
            <div className="pointer-events-auto fixed inset-0 z-[400]">
                <div className="relative h-full w-full">
                    <AnankeLoadingOverlay
                        visible={true}
                        message="Opening…"
                        zIndex={400}
                        overlayProps={{opacity: 0.35}}
                    />
                </div>
            </div>
        ) : null;

    if (isMobile === true) {
        return (
            <>
                {loadingOverlay}
                <BottomNavMobileCreateDrawer
                    opened={opened}
                    onClose={onClose}
                    workspaceId={workspaceId}
                    items={itemsWithEventContainer}
                    onBeforeNavigate={handleBeforeCreateNavigate}
                />
            </>
        );
    }

    if (opened !== true) {
        return loadingOverlay;
    }

    return (
        <>
            {loadingOverlay}
            <WorkspaceListCardCreateSidePanel
                workspaceId={workspaceId}
                items={itemsWithEventContainer}
                onClose={onClose}
                onBeforeNavigate={handleBeforeCreateNavigate}
            />
        </>
    );
}
